import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequestActivationMutation } from '../../store/trialApi';

interface Props {
  vendorEmail: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ActivationRequestModal({ vendorEmail, onClose, onSuccess }: Props) {
  const { t } = useTranslation();
  const [requestActivation, { isLoading }] = useRequestActivationMutation();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setError('');
    try {
      await requestActivation().unwrap();
      setSent(true);
      onSuccess?.();
    } catch (err: unknown) {
      const msg = (err as { data?: { message?: string } })?.data?.message;
      setError(msg || t('signup.errorGeneric'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={!sent ? onClose : undefined} />
      <div className="relative w-full max-w-sm rounded-2xl bg-[#0f1628] border border-white/10 p-6 text-white shadow-2xl">
        {sent ? (
          <>
            <div className="text-3xl mb-3 text-center">🎉</div>
            <h2 className="text-lg font-bold text-center mb-2">{t('activation.successTitle')}</h2>
            <p className="text-sm text-gray-400 text-center mb-6">
              {t('activation.successBody', { email: vendorEmail })}
            </p>
            <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-medium text-sm transition-colors">
              OK
            </button>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold mb-2">{t('activation.title')}</h2>
            <p className="text-sm text-gray-400 mb-6">{t('activation.body')}</p>

            {error && (
              <div className="rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 mb-4">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 py-2.5 rounded-xl border border-white/20 text-gray-300 hover:text-white hover:border-white/40 text-sm font-medium transition-colors"
              >
                {t('activation.cancel')}
              </button>
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold text-sm transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('activation.sending')}
                  </>
                ) : (
                  t('activation.confirm')
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
